import styled from 'styled-components';
import tw from 'twin.macro';

const Container = styled.div`
  ${tw`flex gap-4`}
`;

const Section = styled.div`
  ${tw`flex-1 border border-gray-600 p-4`}
`;

const Title = styled.div`
  ${tw`font-bold mb-4 uppercase`}
`;

const SubSection = styled.div`
  ${tw`mb-4`}
`;

const SubTitle = styled.span`
  ${tw`font-bold`}
`;

const Text = styled.div`
  ${tw`text-sm`}
`;

const NoticeBox = styled.div`
  ${tw`bg-gray-100 p-4 mb-4 border border-gray-600`}
`;

const BulletList = styled.ul`
  ${tw`list-disc pl-6 mb-4`}
`;

const Divider = styled.hr`
  ${tw`border-t border-gray-600 my-6`}
`;

const DoubleDivider = styled.div`
  ${tw`my-6`}
  > hr:first-child {
    ${tw`mb-1`}
  }
`;

const OtherComponentPageOne = () => {
  return (
    <Container>
      <Section>
        <Title>1. FINANCE CHARGE AND PAYMENTS</Title>

        <SubSection>
          <SubTitle>a. How we will figure Finance Charge.</SubTitle>
          <Text>
            We will figure the Finance Charge on a daily basis at the Annual Percentage Rate on the
            unpaid part of the Amount Financed. Seller - Creditor may receive part of the Finance
            Charge.
          </Text>
        </SubSection>

        <SubSection>
          <SubTitle>b. How we will apply payments.</SubTitle>
          <Text>
            We may apply each payment to the earned and unpaid part of the Finance Charge, to the
            unpaid part of the Amount Financed and to other amounts you owe under this contract in
            any order we choose.
          </Text>
        </SubSection>

        <SubSection>
          <SubTitle>c. How late payments or early payments change what you must pay.</SubTitle>
          <Text>
            We based the Payments, and Total Sale Price shown on page 1 of this contract on the
            assumption that you will make every payment on the day it is due. Your Finance Charge,
            Total of Payments, and Total Sale Price will be more if you pay late and less if you pay
            early. Changes may take the form of a larger or smaller final payment or, at our option,
            more or fewer payments of the same amount as your scheduled payment with a smaller final
            payment. We will send you a notice telling you about these changes before the scheduled
            payment is due.
          </Text>
        </SubSection>

        <SubSection>
          <SubTitle>d. You may prepay.</SubTitle>
          <Text>
            You may prepay all or part of the unpaid part of the Amount Financed at any time. If you
            do so, you must pay the earned and unpaid part of the Finance Charge and all other
            amounts due up to the date of your payment. As of the date of your payment, if the
            minimum finance charge is greater than the earned Finance Charge, you may be charged the
            difference; the minimum finance charge is as follows: (1) $25 if the original Amount
            Financed does not exceed $1,000, (2) $50 if the original Amount Financed is more than
            $1,000 but not more than $2,000, or (3) $75 if the original Amount Financed is more than
            $2,000.
          </Text>
        </SubSection>

        <DoubleDivider>
          <Divider />
        </DoubleDivider>

        <Title>2. YOUR OTHER PROMISES TO US</Title>

        <SubSection>
          <SubTitle>a. If the vehicle is damaged, destroyed, or missing.</SubTitle>
          <Text>
            You agree to pay us all you owe under this contract even if the vehicle is damaged,
            destroyed, or missing.
          </Text>
        </SubSection>

        <NoticeBox>
          <Title>GAP LIABILITY NOTICE</Title>
          <Text>
            In the event of theft or damage to your vehicle that results in a total loss, there may
            be a gap between the amount you owe under this contract and the proceeds of your
            insurance settlement and deductible. THIS CONTRACT PROVIDES THAT YOU ARE LIABLE FOR THE
            GAP AMOUNT. An optional debt cancellation agreement for coverage of the gap amount may
            be offered for an additional charge.
          </Text>
        </NoticeBox>

        <SubSection>
          <SubTitle>b. Using the vehicle.</SubTitle>
          <Text>
            You agree not to remove the vehicle from the U.S. or Canada, or to sell, rent, lease, or
            transfer any interest in the vehicle or this contract without our written permission.
            You agree not to expose the vehicle to misuse, seizure, confiscation, or involuntary
            transfer. If we pay any repair bills, storage bills, taxes, fines, or charges on the
            vehicle, you agree to repay the amount when we ask for it.
          </Text>
        </SubSection>

        <SubSection>
          <SubTitle>c. Security Interest.</SubTitle>
          <Text>You give us a security interest in:</Text>
          <BulletList>
            <li>The vehicle and all parts or goods put on it;</li>
            <li>All money or goods received (proceeds) for the vehicle;</li>
            <li>All insurance, maintenance, service, or other contracts we finance for you; and</li>
            <li>
              All proceeds from insurance, maintenance, service, or other contracts we finance for
              you. This includes any refunds of premiums or charges from the contracts.
            </li>
          </BulletList>
          <Text>
            This secures payment of all you owe on this contract. It also secures your other
            agreements in this contract as the law allows. You will make sure the title shows our
            security interest (lien) in the vehicle. You will not allow any other security interest
            to be placed on the title without our written permission.
          </Text>
        </SubSection>
      </Section>

      <Section>
        <SubSection>
          <SubTitle>d. Insurance you must have on the vehicle.</SubTitle>
          <Text>
            You agree to have physical damage insurance covering loss of or damage to the vehicle
            for the term of this contract. The insurance must cover our interest in the vehicle. You
            agree to name us on your insurance policy as loss payee. If you do not have this
            insurance, we may, if we choose, buy physical damage insurance. If we decide to buy
            physical damage insurance, we may either buy insurance that covers your interest and our
            interest in the vehicle, or buy insurance that covers only our interest. If we buy
            either type of insurance, we will tell you which type and the charge you must pay. The
            charge will be the premium for the insurance and a finance charge computed at the Annual
            Percentage Rate shown on page 1 of this contract or, at our option, the highest rate the
            law permits. If the vehicle is lost or damaged, you agree that we may use any insurance
            settlement to reduce what you owe or repair the vehicle.
          </Text>
        </SubSection>

        <SubSection>
          <SubTitle>
            e. What happens to returned insurance, maintenance, service, or other contract charges.
          </SubTitle>
          <Text>
            If we get a refund of insurance, maintenance, service, or other contract charges, you
            agree that we may subtract the refund from what you owe.
          </Text>
        </SubSection>

        <DoubleDivider>
          <Divider />
        </DoubleDivider>

        <Title>3. IF YOU PAY LATE OR BREAK YOUR OTHER PROMISES</Title>

        <SubSection>
          <SubTitle>a. You may owe late charges.</SubTitle>
          <Text>
            You will pay a late charge on each late payment as shown on page 1 of this contract.
            Acceptance of a late payment or late charge does not excuse your late payment or mean
            that you may keep making late payments. If you pay late, we may also take the steps
            described below.
          </Text>
        </SubSection>

        <SubSection>
          <SubTitle>b. You may have to pay all you owe at once.</SubTitle>
          <Text>
            If you break your promises (default), we may demand that you pay all you owe on this
            contract at once, subject to any right the law gives you to reinstate this contract.
          </Text>
          <Text>Default means:</Text>
          <BulletList>
            <li>You do not pay any payment on time;</li>
            <li>
              You give false, incomplete, or misleading information during credit application;
            </li>
            <li>You break any agreements in this contract.</li>
          </BulletList>
          <Text>
            The amount you will owe will be the unpaid part of the Amount Financed plus the earned
            and unpaid part of the Finance Charge, any late charges, and any amounts due because you
            defaulted.
          </Text>
        </SubSection>

        <SubSection>
          <SubTitle>c. You may have to pay collection costs.</SubTitle>
          <Text>
            You will pay our reasonable costs to collect what you owe, including attorney fees,
            court costs, collection agency fees, and fees paid for other reasonable collection
            efforts. You agree to pay a charge not to exceed $15 if any check you give to us is
            dishonored.
          </Text>
        </SubSection>

        <SubSection>
          <SubTitle>d. We may take the vehicle from you.</SubTitle>
          <Text>
            If you default, we may take (repossess) the vehicle from you if we do so peacefully and
            the law allows it. If your vehicle has an electronic tracking device (such as GPS), you
            agree that we may use the device to find the vehicle. If we take the vehicle, any
            accessories, equipment, and replacement parts will stay with the vehicle. If any
            personal items are in the vehicle, we may store them for you. If you do not ask for
            these items back, we may dispose of them as the law allows.
          </Text>
        </SubSection>

        <SubSection>
          <SubTitle>e. How you can get the vehicle back if we take it.</SubTitle>
          <Text>
            If we repossess the vehicle, you may pay to get it back (redeem). You may redeem the
            vehicle by paying all you owe, or you may have the right to reinstate this contract and
            redeem the vehicle by paying past due payments and any late charges, providing proof of
            insurance, and/or taking other action to cure the default. We will provide you all
            notices required by law to tell you when and how much to pay and/or what action you must
            take to redeem the vehicle.
          </Text>
        </SubSection>
      </Section>
    </Container>
  );
};

export default OtherComponentPageOne;
